import { mergeProposalPdfImages } from '@/lib/sales/prepare-pdf-line-images';
/**
 * Public PDF proposal viewer.
 * Fetches the proposal via the public API and renders its Good/Better/Best options
 * with @react-pdf/renderer so the customer (including from the portal) can download
 * or print the tenant's proposal document. Mirrors pdf-invoice / pdf-estimate.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { PDFViewer } from '@react-pdf/renderer';
import { ProposalItemizedPdfDocument } from '@/components/documents/proposals/ProposalItemizedPdfDocument';
import { ProposalSingleScopePdfDocument } from '@/components/documents/proposals/ProposalSingleScopePdfDocument';
import { isSingleScopeLayout } from '@/lib/quote/singleScopeTheme';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { AutoPrintPdf } from '@/components/documents/AutoPrintPdf';
import type { Proposal } from '@/types/proposal';

const OPS_URL = import.meta.env.VITE_OPERATIONS_URL ?? '';

export const Route = createFileRoute('/(public)/pdf-proposal/$id')({
  // `?print=1` (opened by the portal's Print action) auto-prints the document.
  validateSearch: (search: Record<string, unknown>) => ({
    print: search.print === '1' || search.print === true || search.print === 1,
  }),
  component: PdfProposalPage,
});

function PdfProposalPage() {
  const { id } = Route.useParams();
  const { print } = Route.useSearch();

  const { data, isLoading, isError } = useQuery<Proposal>({
    queryKey: ['public', 'proposal-pdf', id],
    queryFn: async () => {
      const res = await fetch(
        `${OPS_URL}/proposals/public/${id}?includeLineItemImageDataUri=true`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (!res.ok) throw new Error('Proposal not found');
      const data: Proposal = await res.json();
      return mergeProposalPdfImages(data, data);
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading proposal…</span>
      </div>
    );
  }

  if (isError || !data) {
    return <DocumentNotFound message="Proposal not found" />;
  }

  const biz = data.company ?? null;
  // A single-scope design prints the single-scope proposal PDF (per-option scope
  // cards); itemized options use the same resolved design as their web tabs.
  const doc = isSingleScopeLayout(data.resolvedDocLayout) ? (
    <ProposalSingleScopePdfDocument proposal={data} company={biz} />
  ) : (
    <ProposalItemizedPdfDocument proposal={data} company={biz} />
  );

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex h-12 items-center gap-2 border-b bg-white px-4 shadow-sm">
        <FileText className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {data.proposalNumber ? `Proposal #${data.proposalNumber}` : 'Proposal'}
        </span>
        {biz?.name ? <span className="text-sm text-gray-400">· {biz.name}</span> : null}
      </div>

      <div className="flex-1">
        <PDFViewer
          width="100%"
          height="100%"
          style={{ border: 'none', minHeight: 'calc(100vh - 48px)' }}
        >
          {doc}
        </PDFViewer>
      </div>
      {print ? <AutoPrintPdf document={doc} /> : null}
    </div>
  );
}
